import React, {
  useEffect,
  useLayoutEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { View, Text, TouchableOpacity } from "react-native";
import { colors } from "src/assets";
import { StackNavigationProp } from "@react-navigation/stack";
import { useNavigation, useRoute } from "@react-navigation/native";
import { EventRegister } from "react-native-event-listeners";
import { RootStackScreenProps } from "src/navigation/types";

import ReactCrop, {
  centerCrop,
  makeAspectCrop,
  Crop,
  PixelCrop,
} from "react-image-crop";

import "react-image-crop/dist/ReactCrop.css";

const TO_RADIANS = Math.PI / 180;

export async function canvasPreview(
  image: HTMLImageElement,
  canvas: HTMLCanvasElement,
  crop: PixelCrop,
  scale = 1,
  rotate = 0
) {
  const ctx = canvas.getContext("2d");

  if (!ctx) {
    throw new Error("No 2d context");
  }

  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;
  // devicePixelRatio slightly increases sharpness on retina devices
  // at the expense of slightly slower render times and needing to
  // size the image back down if you want to download/upload and be
  // true to the images natural size.
  const pixelRatio = window.devicePixelRatio;
  // const pixelRatio = 1

  canvas.width = Math.floor(crop.width * scaleX * pixelRatio);
  canvas.height = Math.floor(crop.height * scaleY * pixelRatio);

  ctx.scale(pixelRatio, pixelRatio);
  ctx.imageSmoothingQuality = "high";

  const cropX = crop.x * scaleX;
  const cropY = crop.y * scaleY;

  const rotateRads = rotate * TO_RADIANS;
  const centerX = image.naturalWidth / 2;
  const centerY = image.naturalHeight / 2;

  ctx.save();

  // 5) Move the crop origin to the canvas origin (0,0)
  ctx.translate(-cropX, -cropY);
  // 4) Move the origin to the center of the original position
  ctx.translate(centerX, centerY);
  // 3) Rotate around the origin
  ctx.rotate(rotateRads);
  // 2) Scale the image
  ctx.scale(scale, scale);
  // 1) Move the center of the image to the origin (0,0)
  ctx.translate(-centerX, -centerY);

  ctx.drawImage(
    image,
    0,
    0,
    image.naturalWidth,
    image.naturalHeight,
    0,
    0,
    image.naturalWidth,
    image.naturalHeight
  );

  ctx.restore();
}

// This is to demonstate how to make and center a % aspect crop
// which is a bit trickier so we use some helper functions.
function centerAspectCrop(
  mediaWidth: number,
  mediaHeight: number,
  aspect: number
) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: "%",
        width: 100, //90,
      },
      aspect,
      mediaWidth,
      mediaHeight
    ),
    mediaWidth,
    mediaHeight
  );
}

export type CustomImageCropNavigationParam = {
  eventName: string;
  file: File;
  aspect?: number;
  // maxSize: Size;
};

type StackNavigationParam = {
  CustomImageCrop: CustomImageCropNavigationParam;
};

// Example from: https://codesandbox.io/s/react-image-crop-demo-with-react-hooks-y831o?file=/src/App.tsx:788-1150
export const CustomImageCropScreen: React.FC = () => {
  const navigation =
    useNavigation<RootStackScreenProps<"CustomImageCrop">["navigation"]>();
  const route = useRoute<RootStackScreenProps<"CustomImageCrop">["route"]>();
  useLayoutEffect(() => {
    navigation.setOptions({ headerShown: false });
  }, [navigation]);

  const [imgSrc, setImgSrc] = useState("");
  const imgRef = useRef<HTMLImageElement>(null);
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [scale] = useState(1);
  const [rotate] = useState(0);
  const [loading, setLoading] = useState<boolean>(false);
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const reader = new FileReader();
    reader.addEventListener("load", (event) => {
      const src = reader.result?.toString();
      if (src) {
        const image = new Image();
        image.onload = function (imageEvent) {
          // Resize the image
          const canvas = document.createElement("canvas");
          let max_size = 1000;
          let width = image.width;
          let height = image.height;
          if (width > height) {
            if (width > max_size) {
              height *= max_size / width;
              width = max_size;
            }
          } else {
            if (height > max_size) {
              width *= max_size / height;
              height = max_size;
            }
          }
          canvas.width = width;
          canvas.height = height;
          canvas.getContext("2d")?.drawImage(image, 0, 0, width, height);

          setImgSrc(canvas.toDataURL() || "");
        };
        image.src = src;
      }

      // setImgSrc(reader.result?.toString() || "");
    });
    reader.readAsDataURL(route.params.file);
  }, [route.params.file]);

  function onImageLoad(e: React.SyntheticEvent<HTMLImageElement>) {
    const { width, height } = e.currentTarget;
    setCrop(
      centerAspectCrop(width, height, route.params.aspect || width / height)
    );
  }

  const cancelHandler = useCallback(() => {
    navigation.goBack();
  }, [navigation]);

  const doneHandler = useCallback(() => {
    if (
      completedCrop?.width &&
      completedCrop?.height &&
      imgRef.current &&
      previewCanvasRef.current
    ) {
      canvasPreview(
        imgRef.current,
        previewCanvasRef.current,
        completedCrop,
        scale,
        rotate
      );

      const dataURL = previewCanvasRef.current.toDataURL("image/jpeg");
      EventRegister.emit(route.params.eventName, { dataURL });
      setLoading(false);
      navigation.goBack();
    }
  }, [navigation, route.params.eventName, completedCrop, scale, rotate]);

  return (
    <View style={{ flex: 1, backgroundColor: colors.chokuBlack }}>
      <View
        style={{ padding: 16, justifyContent: "center", alignItems: "center" }}
      >
        <Text style={{ color: colors.white }}>編集</Text>
      </View>
      <View style={{ flex: 1, justifyContent: "center", alignItems: "center" }}>
        {!!imgSrc && (
          <ReactCrop
            crop={crop}
            onChange={(_, percentCrop) => setCrop(percentCrop)}
            onComplete={(c) => setCompletedCrop(c)}
            aspect={route.params.aspect}
          >
            <img
              ref={imgRef}
              alt="Crop me"
              src={imgSrc}
              style={{ transform: `scale(${scale}) rotate(${rotate}deg)` }}
              onLoad={onImageLoad}
            />
          </ReactCrop>
        )}
        {!!completedCrop && (
          <canvas
            ref={previewCanvasRef}
            style={{
              objectFit: "contain",
              width: completedCrop.width,
              height: completedCrop.height,
              display: "none",
            }}
          />
        )}
      </View>
      <View
        style={{
          padding: 8,
          flexDirection: "row",
          justifyContent: "space-between",
        }}
      >
        <TouchableOpacity style={{ padding: 16 }} onPress={cancelHandler}>
          <Text style={{ fontWeight: "700", color: colors.white }}>
            キャンセル
          </Text>
        </TouchableOpacity>
        {loading ? (
          <View style={{ padding: 8 }}>
            <Text style={{ fontWeight: "700", color: colors.chokuGray }}>
              完了
            </Text>
          </View>
        ) : (
          <TouchableOpacity style={{ padding: 8 }} onPress={doneHandler}>
            <Text style={{ fontWeight: "700", color: colors.white }}>完了</Text>
          </TouchableOpacity>
        )}
      </View>
    </View>
  );
};
